import { Box, Button, Checkbox, Chip, Divider } from '@mui/material';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { MatrixCriterion, MatrixCriterionValue } from '../../../../types/advise';
import { criterionCount } from './FeatureMatrixPanel.calc';
import { selectionValue } from './CriteriaWeighting';
import { MatrixAddControl } from './MatrixAddControl';
import { MatrixCategoryBlock } from './MatrixCategoryBlock';
import { NoChecklistCallout } from './NoChecklistCallout';

export interface DetailedFunctionalityMatrixProps {
  criteria: MatrixCriterion[];
  canEdit: boolean;
  onChangeCriterion: (criterionId: string, value: MatrixCriterionValue) => void;
  onSelectFeature: (featureId: string, selected: boolean) => void;
  onBack: () => void;
}

export const DetailedFunctionalityMatrix = ({
  criteria,
  canEdit,
  onChangeCriterion,
  onSelectFeature,
  onBack
}: DetailedFunctionalityMatrixProps) => (
  <Box data-testid="detailed-functionality-matrix">
    <Box className="flex flex-wrap items-center justify-between gap-2 py-3">
      <TextSpan
        size="sm14"
        color="grey6">
        Select the features required for this engagement, grouped by evaluation criterion. Selections update the Feature
        Matrix comparison and supplier scoring automatically.
      </TextSpan>
      <Button
        size="small"
        onClick={onBack}>
        ← Functionality Matrix overview
      </Button>
    </Box>
    {canEdit && (
      <Box className="flex justify-end mb-2">
        <MatrixAddControl label="Add Custom Category" />
      </Box>
    )}
    <Divider />
    {criteria.map((criterion) => {
      const count = criterionCount(criterion);
      const noFeatures = count.total + count.custom === 0;
      return (
        <Box
          key={criterion.id}
          className="px-6 py-4"
          data-testid={`matrix-criterion-${criterion.key}`}>
          <Box className="flex items-center gap-2">
            <Checkbox
              size="small"
              checked={criterion.selected}
              disabled={!canEdit}
              onChange={(event) => onChangeCriterion(criterion.id, selectionValue(event.target.checked))}
              inputProps={{ 'aria-label': criterion.label }}
            />
            <TextSpan
              size="sm14"
              weight="semibold"
              color={criterion.selected ? 'grey8' : 'grey5'}
              className="flex-1">
              {criterion.label}
              {criterion.custom && (
                <Chip
                  size="small"
                  label="CUSTOM CRITERION"
                  color="primary"
                  variant="outlined"
                  className="ml-2"
                />
              )}
            </TextSpan>
            <TextSpan
              size="sm14"
              weight="medium"
              color={noFeatures ? 'orange' : 'indigo'}>
              {noFeatures ? 'No features mapped' : `${count.selected} of ${count.total} selected`}
            </TextSpan>
          </Box>
          <TextBlock
            size="sm14"
            color="grey6"
            className="ml-10">
            {criterion.custom
              ? 'Custom criterion — set its weight on the Criteria Weighting step'
              : criterion.description}
          </TextBlock>
          <Box className="ml-10">
            {noFeatures ? (
              <NoChecklistCallout
                criterion={criterion}
                canEdit={canEdit}
              />
            ) : (
              criterion.categories.map((category) => (
                <MatrixCategoryBlock
                  key={category.id}
                  category={category}
                  canEdit={canEdit}
                  onSelectFeature={onSelectFeature}
                />
              ))
            )}
          </Box>
          <Divider className="mt-4" />
        </Box>
      );
    })}
  </Box>
);
